// Import dependencies
import Cookies from 'js-cookie';
import initialiser from '../../utils/initialiser';

import { getMetaDataCalledEvent, sessionIsInactiveEvent } from '../CreateYourOwnPackage/utils';

const defaultSettings = {
  activeClass: 'is-active',
  activeClassHeader: 'o-section--has-timer',
  timerHeader: '.js-timer-header',
  testMode: false,
  testDuration: 10, // 10 seconds for test mode
  expiredModalId: 'timer-expired-modal',
};

class CartTimerManager {
  constructor(config) {
    const { el, settings } = config;

    // Set root element
    this.container = el;

    // Set settings
    this.settings = { ...defaultSettings, ...settings };
    console.log('CartTimerManager initialized with settings:', this.settings);

    // DOM Elements
    this.cartLink = document.querySelector(this.settings.cartLink);
    this.cartLinkMobile = document.querySelector(this.settings.cartLinkMobile);
    this.cartTimerSelector = document.querySelector(this.settings.selector);
    this.cartTimerPlaceholder = document.querySelector(this.settings.cartTimerPlaceholder);
    this.tnew = this.container.dataset.tnew;
    this.cartCount = document.createElement('strong');
    this.cartCountMobile = document.createElement('strong');
  }

  // Initialisation Function - Called on each component instance intialisation
  init = () => {
    if (!this.container) {
      console.error('CartTimerManager: Container element not found');
      return;
    }

    console.log('CartTimerManager: init() called');
    // Call bind events
    this.bindEvents();

    // Check if we're on the test page
    if (window.location.href.includes('https://sso.test/create-your-own')) {
      if (this.settings.testMode) {
        console.log('CartTimerManager: Starting test timer on create-your-own page');
        this.startTestTimer();
      } else {
        console.log('CartTimerManager: Test timer disabled. Enable with window.enableTestTimer() or ?test-timer=true');
      }
    }
  };

  bindEvents = () => {
    window.addEventListener('load', () => this.handleCartTimer());
    window.addEventListener('performanceAdded', () => this.getMetaData(Cookies.get('sessionKey')));
    window.addEventListener('performanceRemoved', () => this.getMetaData(Cookies.get('sessionKey')));
  };

  startTestTimer = () => {
    // Create a mock timer for testing purposes
    if (!this.cartTimerSelector || !this.cartTimerPlaceholder) {
      console.error('CartTimerManager: Required DOM elements not found for test timer');
      return;
    }

    this.cartTimerSelector.classList.add(this.settings.activeClass);
    console.log('Starting test timer for', this.settings.testDuration, 'seconds');

    // Set the current time
    const now = new Date().getTime();

    // Set the expiration time to 10 seconds from now
    const testExpirationTime = now + (this.settings.testDuration * 1000);

    // Start the countdown
    this.startCountdown(testExpirationTime);
  };

  startCountdown = (expirationTime) => {
    if (!this.cartTimerPlaceholder) {
      console.error('CartTimerManager: cartTimerPlaceholder not found');
      return;
    }

    console.log('CartTimerManager: Starting countdown to', new Date(expirationTime).toLocaleTimeString());

    // Update the count down every 1 second
    const interval = setInterval(() => {
      // Get current time
      const now = new Date().getTime();

      // Find the distance between now and the expiration time
      const distance = expirationTime - now;

      // Time calculations for minutes and seconds
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000).toLocaleString('en-US', {
        minimumIntegerDigits: 2,
        useGrouping: false,
      });

      // Display the result
      this.cartTimerPlaceholder.innerHTML = `Your cart expires in ${minutes}:${seconds} minutes`;

      // If the count down is finished
      if (distance <= 0) {
        clearInterval(interval);
        if (this.cartLink) this.cartLink.classList.remove(this.settings.activeClass);
        if (this.cartLinkMobile) this.cartLinkMobile.classList.remove(this.settings.activeClass);
        this.cartTimerPlaceholder.innerHTML = 'Your cart has expired';

        // Open modal when timer expires
        this.openExpiredModal();
      }
    }, 1000);
  };

  openExpiredModal = () => {
    console.log('CartTimerManager: Opening expired modal with ID', this.settings.expiredModalId);

    // Find the modal element
    const modal = document.getElementById(this.settings.expiredModalId);

    if (modal) {
      console.log('CartTimerManager: Found modal element');

      // First, make the modal visible by removing the inline style
      modal.style.display = 'block';

      // If using CYOP modal
      if (modal.classList.contains('c-cyop__modal')) {
        console.log('CartTimerManager: Opening CYOP modal');
        document.body.classList.add('has-modal-opened');
        modal.classList.add('c-cyop__modal-show');

        // Add event listeners for closing the modal only if they haven't been added yet
        const closeButtons = modal.querySelectorAll('.js-modal-close');
        closeButtons.forEach((button) => {
          // Remove any existing event listeners to prevent duplicates
          button.removeEventListener('click', this.handleCloseModal);
          // Add new event listener
          button.addEventListener('click', this.handleCloseModal);
        });

        // Add event listener for reset session button
        const resetSessionBtn = modal.querySelector('.js-reset-session-btn');
        if (resetSessionBtn) {
          // Remove any existing event listeners to prevent duplicates
          resetSessionBtn.removeEventListener('click', this.resetSession);
          // Add new event listener
          resetSessionBtn.addEventListener('click', this.resetSession);
        }

        // Add keyboard event listener for ESC key (only once)
        document.removeEventListener('keydown', this.handleEscapeKey);
        document.addEventListener('keydown', this.handleEscapeKey);
      } else if (modal.classList.contains('c-modal')) {
        // If using vanilla JS modal
        console.log('CartTimerManager: Opening vanilla JS modal');
        modal.classList.add('is-visible');
        document.body.classList.add('has-modal-opened');
        document.body.classList.add('has-overlay');

        // Add event listeners for closing the modal
        const closeButtons = modal.querySelectorAll('.js-modal-close');
        closeButtons.forEach((button) => {
          // Remove any existing event listeners to prevent duplicates
          button.removeEventListener('click', this.handleCloseModal);
          // Add new event listener
          button.addEventListener('click', this.handleCloseModal);
        });
      } else if (window.createYourOwnPackage && typeof window.createYourOwnPackage.showModal === 'function') {
        // If using React modal
        console.log('CartTimerManager: Opening React modal');
        window.createYourOwnPackage.showModal({
          type: 'critical-error',
          heading: 'Your session has expired',
          text: 'Your cart timer has expired. Please reset your session to create your own package.',
        });
      }
    } else {
      console.error('CartTimerManager: Modal element not found with ID', this.settings.expiredModalId);
    }
  };

  handleCloseModal = (e) => {
    const modal = document.getElementById(this.settings.expiredModalId);
    if (modal) {
      this.closeExpiredModal(modal);
    }
  };

  handleEscapeKey = (e) => {
    if (e.key === 'Escape') {
      const modal = document.getElementById(this.settings.expiredModalId);
      if (modal) {
        this.closeExpiredModal(modal);
      }
    }
  };

  closeExpiredModal = (modal) => {
    console.log('CartTimerManager: Closing modal');

    if (modal.classList.contains('c-cyop__modal')) {
      modal.classList.remove('c-cyop__modal-show');
    } else if (modal.classList.contains('c-modal')) {
      modal.classList.remove('is-visible');
      document.body.classList.remove('has-overlay');
    }

    document.body.classList.remove('has-modal-opened');

    // Hide the modal completely
    modal.style.display = 'none';
  };

  resetSession = () => {
    console.log('CartTimerManager: Resetting session');

    // Prevent multiple clicks
    const resetBtn = document.getElementById('reset-session-btn');
    if (resetBtn && resetBtn.disabled) {
      console.log('Reset already in progress, ignoring click');
      return;
    }

    // Show loading state on the button
    if (resetBtn) {
      resetBtn.innerHTML = 'Resetting...';
      resetBtn.disabled = true;
    }

    // Call the clear session endpoint
    fetch('/tessitura/clear-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Session reset failed');
        }
        return response.json();
      })
      .then((data) => {
        console.log('Session reset successful:', data);
        // Reload the page to start with a fresh session
        window.location.reload();
      })
      .catch((error) => {
        console.error('Error resetting session:', error);
        // Reset the button state
        if (resetBtn) {
          resetBtn.innerHTML = 'Reset Session';
          resetBtn.disabled = false;
        }
      });
  };

  handleCartTimer = () => {
    console.log('CartTimerManager: handleCartTimer() called');

    fetch(this.tnew, {
      mode: 'cors',
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((metaData) => this.getMetaData(metaData.encryptedSessionKey))
      .catch((err) => {
        const errorMessage = document.querySelector('.js-create-your-own-package-sessionkey-error');
        if (errorMessage) errorMessage.classList.remove('u-hide');

        console.error(`Fetch problem: ${err.message}`); // eslint-disable-line
      });
  };

  getMetaData = (sessionKey) => {
    Cookies.set('sessionKey', sessionKey);

    fetch('/tessitura/getMetaData', {
      method: 'get',
      mode: 'cors',
      credentials: 'include',
      headers: new Headers({
        Accept: 'application/json',
        Authorization: `Bearer ${sessionKey}`,
        'Content-Type': 'application/json',
      }),
    }).then((response) => response.json()).then((data) => {
      window.dispatchEvent(getMetaDataCalledEvent);

      if (data.cartCount > 0) {
        if (this.cartLink) this.cartLink.classList.add(this.settings.activeClass);
        if (this.cartLinkMobile) this.cartLinkMobile.classList.add(this.settings.activeClass);
        if (this.container.parentElement) {
          const timerHeader = this.container.parentElement.querySelector(this.settings.timerHeader);
          if (timerHeader) timerHeader.classList.add(this.settings.activeClassHeader);
        }

        this.cartCount.innerHTML = data.cartCount;
        this.cartCountMobile.innerHTML = data.cartCount;

        if (this.cartLink) this.cartLink.insertBefore(this.cartCount, this.cartLink.firstChild);
        if (this.cartLinkMobile) this.cartLinkMobile.insertBefore(this.cartCountMobile, this.cartLinkMobile.firstChild);
      }

      if (typeof data.expiration !== 'undefined') {
        if (this.cartTimerSelector) this.cartTimerSelector.classList.add(this.settings.activeClass);

        // Get expiry date and time
        const countDownDate = data.expiration === ''
          ? new Date(new Date().getTime() - 1)
          : new Date(data.expiration).getTime();

        this.startCountdown(countDownDate);
      } else {
        if (this.cartLink) this.cartLink.classList.remove(this.settings.activeClass);
        if (this.cartLinkMobile) this.cartLinkMobile.classList.remove(this.settings.activeClass);
        if (this.cartTimerPlaceholder) this.cartTimerPlaceholder.innerHTML = '';
      }

      if (data.activeSession === false) {
        window.dispatchEvent(sessionIsInactiveEvent);
      }
    });
  }
}

// Create a global reference to the component instances
if (typeof window !== 'undefined') {
  window.componentInstances = window.componentInstances || {};
  window.componentInstances.CartTimerManager = window.componentInstances.CartTimerManager || [];
}

// Export component wrapped in initialiser utility
export default (settings) => {
  const instance = initialiser(settings, CartTimerManager);

  // Store instance in global reference
  if (instance && typeof window !== 'undefined') {
    window.componentInstances.CartTimerManager.push(instance);
  }

  return instance;
};
